import { recoverMessageAddress, sha256, toHex, type Hex } from "viem";
import config from "@/config/config";

export const TERMS_VERSION = "4.2";
/** Raise this only when an update must be signed again. Text and version edits stay covered by the existing signature. */
export const TERMS_CONSENT_GENERATION = 1;
const TERMS_TEXT_URL = "/legal/terms-of-service-v4.2.txt";
const STORAGE_PREFIX = "playmafia:terms:signature";

export type TermsDocument = {
  text: string;
  hash: Hex;
  version: string;
  chainId: number;
};

type SavedAgreement = {
  account: string;
  chainId: number;
  version: string;
  hash: Hex;
  signature: Hex;
  signedAt: string;
  consentGeneration?: number;
};

export async function loadTermsDocument(): Promise<TermsDocument> {
  const response = await fetch(TERMS_TEXT_URL, { cache: "no-store" });
  if (!response.ok) throw new Error("Could not load the Terms of Service");
  const text = await response.text();
  const hash = sha256(toHex(text));
  return { text, hash, version: TERMS_VERSION, chainId: config.chainId };
}

export function termsAgreementMessage(document: TermsDocument, account: string): string {
  const domain = document.chainId === 369 ? "pls.playmafia.io" : "bnb.playmafia.io";
  return [
    `${domain} Terms of Service Agreement`,
    `Version: ${document.version}`,
    `Chain ID: ${document.chainId}`,
    `Wallet: ${account.toLowerCase()}`,
    `Document SHA-256: ${document.hash}`,
    "By signing, I agree to these Terms of Service.",
  ].join("\n");
}

export function termsStorageKey(chainId: number, account: string): string {
  return [STORAGE_PREFIX, chainId, account.toLowerCase()].join(":");
}

function isAgreementKey(key: string, chainId: number, account: string): boolean {
  const prefix = `${STORAGE_PREFIX}:${chainId}:`;
  return key.startsWith(prefix) && key.endsWith(`:${account.toLowerCase()}`);
}

function readSavedAgreements(chainId: number, account: string): SavedAgreement[] {
  const records: SavedAgreement[] = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (!key || !isAgreementKey(key, chainId, account)) continue;
    try {
      records.push(JSON.parse(localStorage.getItem(key) || "") as SavedAgreement);
    } catch {
      continue;
    }
  }
  return records;
}

async function signatureMatches(document: TermsDocument, account: string, signature: Hex): Promise<boolean> {
  try {
    const recovered = await recoverMessageAddress({ message: termsAgreementMessage(document, account), signature });
    return recovered.toLowerCase() === account.toLowerCase();
  } catch {
    return false;
  }
}

export async function hasSavedTermsAgreement(document: TermsDocument, account: string): Promise<boolean> {
  for (const record of readSavedAgreements(document.chainId, account)) {
    const generation = record.consentGeneration ?? 1;
    if (
      record.account?.toLowerCase() !== account.toLowerCase() ||
      record.chainId !== document.chainId ||
      generation !== TERMS_CONSENT_GENERATION ||
      typeof record.version !== "string" ||
      typeof record.hash !== "string" ||
      typeof record.signature !== "string"
    ) continue;
    const signed = await signatureMatches(
      { text: "", hash: record.hash, version: record.version, chainId: record.chainId },
      account,
      record.signature,
    );
    if (signed) return true;
  }
  return false;
}

export async function saveTermsAgreement(document: TermsDocument, account: string, signature: Hex): Promise<void> {
  if (!await signatureMatches(document, account, signature)) {
    throw new Error("The signature did not match the connected wallet");
  }
  const record: SavedAgreement = {
    account: account.toLowerCase(),
    chainId: document.chainId,
    version: document.version,
    hash: document.hash,
    signature,
    signedAt: new Date().toISOString(),
    consentGeneration: TERMS_CONSENT_GENERATION,
  };
  localStorage.setItem(termsStorageKey(document.chainId, account), JSON.stringify(record));
}
